// client/src/components/first-run-tour.tsx
// v7.44 — FIRST-RUN TOUR: the numbered pins on the New Task screen.
//
// Runs AFTER the v7.07 wizard (which already got the user a brand), the first
// time they land on New Task. Seven anchored popovers between a welcome card
// and a finish card whose one action pre-fills the composer with their first
// prompt. Every anchor lives on the New Task screen, so nothing navigates
// away mid-tour. Spec: "AdMoji First-Run Tour" artifact (Sep 4, 2026).
//
// Two ways in (canvas-store.tourRequest):
//   'auto'   — first sign-in, non-admins only. Finish AND skip stamp
//              users.tour_completed_at so it never nags twice.
//   'forced' — the admin "Start tour" Dev Tools item. Never stamps anything.
//
// The fork: a user who skipped the wizard has ZERO brands and the composer is
// already covered by the no-brand gate. Touring seven features they cannot
// use is noise, so the tour collapses to one amber step pinned on the gate's
// button, stamps nothing on "Add brand", and re-arms once a brand exists.
//
// Built in-house (fixed-position card + SVG spotlight mask) rather than a
// tour library: every library still needs its card replaced to get our
// styling, a counter and a <video>, so positioning was all we would buy.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { TourVideoSlot as VideoSlot } from './tour-video-slot';
import { restoredTourIndex } from './tour-progress';
import { useCanvasStore } from '@/pages/canvas/canvas-store';

type Side = 'right' | 'left' | 'top' | 'bottom';

interface TourStep {
  key: string;
  /** Element the pin points at. First selector that resolves wins. */
  anchors: string[];
  /** Extra elements swept into the spotlight (union of rects). */
  spotlight?: string[];
  separateSpotlights?: boolean;
  side: Side;
  title: string;
  body: string;
  /** Placeholder clip name — replaced by a real <video> when the file lands. */
  video?: string;
}

interface Props {
  mode: 'auto' | 'forced';
  hasBrand: boolean;
  maxBrands: number | null;
  /** Drop text into the composer and focus it — does NOT send. */
  onPrefill: (text: string) => void;
  /** The no-brand fork's one action (opens the Brands view). */
  onAddBrand: () => void;
  onClose: () => void;
}

const PINK = '#A9315B';
const LEGACY_STEP_KEY = 'admoji-tour-step';
const STEP_KEY = 'admoji-tour-step-v2';
const CARD_W = 340;
const GAP = 14;
const PAD = 8;

// v7.45 — copy is deliberately GENERIC. It used to interpolate the user's
// real brand and product names, which meant test data ("Nello", "SuperCALM")
// showed up in the tour. "my brand" reads fine for everyone and needs no
// lookup, so the products fetch went away with it.
function buildSteps(maxBrands: number | null): TourStep[] {
  const cap = maxBrands == null ? 'Your plan sets how many.' : maxBrands === 1 ? 'Your plan includes one.' : `Your plan includes ${maxBrands === 2 ? 'two' : maxBrands}.`;
  return [
    {
      key: 'brand',
      anchors: ['[data-testid^="brand-folder-"]', '[data-testid="brands-section-add"]', '[data-testid="nav-brands"]'],
      side: 'right',
      spotlight: ['[data-testid="nav-brands"]'],
      separateSpotlights: true,
      title: 'Brands',
      body: `AdMoji reads your site — products, voice, visuals and competitors — so every ad starts with your brand. Manage the details in Brands; open its project folder to see your tasks. ${cap}`,
      video: 'tour-01-brand.mp4',
    },
    {
      key: 'prompt',
      anchors: ['[data-testid="canvas-chat-input"]'],
      spotlight: ['[data-testid="media-tab-image"]', '[data-testid="canvas-chat-send"]', '[data-testid="ratio-selector-btn"]'],
      side: 'top',
      title: 'Ask for anything',
      body: 'Type it like a brief: "Make five ads for my brand, for Black Friday, include 5 star reviews." Plain English works.',
      video: 'tour-03-prompt.mp4',
    },
    {
      // v7.46 — the + is where a brand gets attached to THIS task. It sits
      // before the dials on purpose: what you are making ads FOR matters
      // more than which model renders them.
      key: 'attach',
      anchors: ['[data-testid="input-attach"]'],
      side: 'bottom',
      title: 'Add your brand to the task',
      body: 'The plus button attaches your brand to this task — along with files, photos or a saved set of ad angles. Whatever is attached is what AdMoji builds from.',
      video: 'tour-attach-brand.mp4',
    },
    {
      key: 'dials',
      anchors: ['[data-testid="ratio-selector-btn"]', '[data-testid="image-model-selector-btn"]'],
      spotlight: ['[data-testid="image-model-selector-btn"]', '[data-testid="image-tier-selector-btn"]', '[data-testid="composer-advanced-btn"]', '[data-testid="ratio-selector-btn"]'],
      side: 'bottom',
      title: 'Sizes and the image model',
      body: 'Pick ad sizes and the image model here. Advanced locks specific angles and styles. Leave it all alone to start — AdMoji picks a good mix.',
      video: 'tour-04-dials.mp4',
    },
    {
      key: 'discover',
      anchors: ['[data-testid="nav-discover"]'],
      side: 'right',
      title: 'Discover',
      body: 'Winning ads, sorted by format — before-and-after, UGC, listicles, sales. Pick one and AdMoji rebuilds it around your brand.',
      video: 'tour-05-discover.mp4',
    },
    {
      key: 'assets',
      anchors: ['[data-testid="nav-assets"]'],
      side: 'right',
      title: 'Assets',
      body: 'Everything you generate lands here, filed by brand. Download anything, any time.',
    },
    {
      key: 'analytics',
      anchors: ['[data-testid="nav-analytics"]'],
      side: 'right',
      title: 'Analytics',
      body: 'Connect your Meta account and AdMoji learns what actually wins — then feeds it back into the next batch.',
    },
  ];
}

const FORK_STEP: TourStep = {
  key: 'add-brand',
  anchors: ['[data-testid="no-brand-gate-add-brand"]', '[data-testid="no-brand-gate"]'],
  side: 'top',
  title: 'Add your brand first',
  body: 'Start here. Paste your website and AdMoji reads it in about thirty seconds — products, voice, visuals. Nothing else works until this does.',
  video: 'tour-00-addbrand.mp4',
};

type Rect = { top: number; left: number; width: number; height: number };

function unionRect(els: Element[]): Rect | null {
  const rs = els.map((e) => e.getBoundingClientRect()).filter((r) => r.width > 0 && r.height > 0);
  if (!rs.length) return null;
  const top = Math.min(...rs.map((r) => r.top));
  const left = Math.min(...rs.map((r) => r.left));
  const bottom = Math.max(...rs.map((r) => r.bottom));
  const right = Math.max(...rs.map((r) => r.right));
  return { top, left, width: right - left, height: bottom - top };
}

function resolve(selectors: string[] | undefined): Element[] {
  if (!selectors) return [];
  const out: Element[] = [];
  for (const s of selectors) {
    const el = document.querySelector(s);
    if (el) out.push(el);
  }
  return out;
}

/** Card position beside the spotlight.
 *
 *  v7.45 — the old version flipped sides with a chain of ifs and then clamped
 *  BOTH axes into the viewport, which parked the card straight on top of the
 *  thing it was pointing at (Dan: the sizes/model card covered the dials).
 *  A 'top' preference could even double-flip back to 'top' with no room.
 *  Now: pick the first side where the card genuinely CLEARS the spotlight,
 *  and clamp only the free axis so it can never slide back across the cutout. */
function placeCard(spot: Rect, side: Side, cardH: number): { top: number; left: number; side: Side } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const M = 12; // viewport margin
  const room: Record<Side, boolean> = {
    right: vw - (spot.left + spot.width + GAP) - M >= CARD_W,
    left: spot.left - GAP - M >= CARD_W,
    top: spot.top - GAP - M >= cardH,
    bottom: vh - (spot.top + spot.height + GAP) - M >= cardH,
  };
  const order: Side[] = [side, 'right', 'left', 'bottom', 'top'];
  const s = order.find((o) => room[o]) ?? side;
  let top: number;
  let left: number;
  if (s === 'right' || s === 'left') {
    left = s === 'right' ? spot.left + spot.width + GAP : spot.left - GAP - CARD_W;
    top = Math.max(M, Math.min(spot.top + spot.height / 2 - cardH / 2, vh - cardH - M));
  } else {
    top = s === 'bottom' ? spot.top + spot.height + GAP : spot.top - GAP - cardH;
    left = Math.max(M, Math.min(spot.left + spot.width / 2 - CARD_W / 2, vw - CARD_W - M));
  }
  // Only bites when the card fits nowhere at all — better on-screen than not.
  return {
    top: Math.max(M, Math.min(top, vh - cardH - M)),
    left: Math.max(M, Math.min(left, vw - CARD_W - M)),
    side: s,
  };
}

const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: 'none',
  background: '#161616', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const ghost: React.CSSProperties = { ...btn, background: 'transparent', color: '#161616', border: '1px solid rgba(17,17,17,0.18)' };

export function FirstRunTour({ mode, hasBrand, maxBrands, onPrefill, onAddBrand, onClose }: Props) {
  const setTourActive = useCanvasStore((s) => s.setTourActive);
  const setSidebarCollapsed = useCanvasStore((s) => s.setSidebarCollapsed);
  const wasCollapsed = useRef(useCanvasStore.getState().isSidebarCollapsed);

  const steps = useMemo(() => (hasBrand ? buildSteps(maxBrands) : [FORK_STEP]), [hasBrand, maxBrands]);

  // -1 = cover, 0..n-1 = pins, n = finish (full tour only).
  const [idx, setIdx] = useState<number>(() => {
    if (mode !== 'auto') return -1;
    try {
      return restoredTourIndex(localStorage.getItem(STEP_KEY), localStorage.getItem(LEGACY_STEP_KEY), steps.length);
    } catch { return -1; }
  });
  const [cutouts, setCutouts] = useState<Rect[]>([]);
  const [card, setCard] = useState<{ top: number; left: number; side: Side } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const isCover = idx === -1;
  const isFinish = hasBrand && idx >= steps.length;
  const step = !isCover && !isFinish ? steps[idx] : null;

  // Own the screen: flag for App.tsx (banner), sidebar open for the pins.
  useEffect(() => {
    setTourActive(true);
    if (wasCollapsed.current) setSidebarCollapsed(false);
    return () => {
      setTourActive(false);
      if (wasCollapsed.current) setSidebarCollapsed(true);
    };
  }, [setTourActive, setSidebarCollapsed]);

  useEffect(() => {
    if (mode !== 'auto') return;
    try { localStorage.setItem(STEP_KEY, String(idx)); } catch { /* private mode */ }
  }, [idx, mode]);

  const stamp = useCallback((skipped: boolean) => {
    try { localStorage.removeItem(STEP_KEY); localStorage.removeItem(LEGACY_STEP_KEY); } catch { /* ignore */ }
    if (mode !== 'auto') return;
    fetch('/api/tour/complete', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skipped, lastStep: idx }),
    }).catch(() => { /* never block the UI on this */ });
  }, [mode, idx]);

  const finish = useCallback(() => { stamp(false); onClose(); }, [stamp, onClose]);
  const skip = useCallback(() => { stamp(true); onClose(); }, [stamp, onClose]);
  const next = useCallback(() => setIdx((i) => Math.min(i + 1, steps.length)), [steps.length]);
  const back = useCallback(() => setIdx((i) => Math.max(i - 1, -1)), []);

  // Measure the anchor; re-measure on resize/scroll and as layout settles.
  const measure = useCallback(() => {
    if (!step) { setCutouts([]); setCard(null); return; }
    const anchorEls = resolve(step.anchors);
    const anchor = anchorEls[0];
    if (!anchor) { setCutouts([]); setCard(null); return; }
    const all = [anchor, ...resolve(step.spotlight)];
    const r = unionRect(all);
    if (!r) { setCutouts([]); setCard(null); return; }
    const padded = { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
    setCutouts(step.separateSpotlights
      ? Array.from(new Set(all)).map((el) => unionRect([el])).filter((r): r is Rect => r !== null).map((r) => ({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 }))
      : [padded]);
    const cardH = cardRef.current?.offsetHeight ?? 220;
    setCard(placeCard(padded, step.side, cardH));
  }, [step]);

  useLayoutEffect(() => {
    // v7.45 — every step starts with the menu scrolled to the top, so the
    // pins land where the copy says they do. Must happen BEFORE measure(),
    // or the rects are one scroll position stale.
    const sidebar = document.querySelector('[data-testid="sidebar-scroll"]');
    if (sidebar) sidebar.scrollTop = 0;
    if (!step) return;
    const anchor = resolve(step.anchors)[0];
    // Only pull an anchor into view when it is OUTSIDE the sidebar — doing it
    // for a nav item would undo the scroll-to-top we just did.
    if (anchor && !(sidebar && sidebar.contains(anchor))) {
      anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    measure();
    // Second pass once the card has its real height / fonts have settled.
    const t1 = window.setTimeout(measure, 40);
    const t2 = window.setTimeout(measure, 250);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, [step, measure, idx]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => { window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [measure]);

  // Keyboard: → / Enter = next, ← = back, Esc = skip.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); skip(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { if (!isFinish) { e.preventDefault(); next(); } }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, back, skip, isFinish]);

  const total = steps.length;
  const counter = step ? `${idx + 1}/${total}` : '';

  // ── Mask: dims everything, with a rounded cutout over the spotlight. ──
  const mask = (
    <svg
      data-testid="tour-mask"
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 9000, pointerEvents: 'auto' }}
      onClick={(e) => e.stopPropagation()}
    >
      <defs>
        <mask id="tour-spot-mask">
          <rect x="0" y="0" width="100%" height="100%" fill="#fff" />
          {cutouts.map((r, i) => <rect key={i} data-testid="tour-spot-cutout" x={r.left} y={r.top} width={r.width} height={r.height} rx="12" fill="#000" />)}
        </mask>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill="rgba(10,10,10,0.55)" mask="url(#tour-spot-mask)" />
      {cutouts.map((r, i) => <rect key={i} x={r.left} y={r.top} width={r.width} height={r.height} rx="12" fill="none" stroke={PINK} strokeWidth="2" />)}
    </svg>
  );

  const pin = (n: number | string, amber = false) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%',
      background: amber ? '#9A6414' : PINK, color: '#fff', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, fontWeight: 600, flexShrink: 0,
    }}>{n}</span>
  );

  const cardBase: React.CSSProperties = {
    position: 'fixed', zIndex: 9001, width: CARD_W, background: '#FFFFFF', color: '#161616', borderRadius: 14,
    boxShadow: '0 18px 48px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.12)', padding: '16px 18px 14px', fontSize: 14, lineHeight: 1.5,
  };

  // ── Cover ──
  if (isCover) {
    return (
      <>
        {mask}
        <div data-testid="tour-cover" style={{ ...cardBase, width: 440, left: '50%', top: '50%', transform: 'translate(-50%, -50%)', padding: '22px 24px 18px' }}>
          <VideoSlot file="tour-welcome.mp4" wide />
          <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', marginBottom: 6 }}>Welcome to AdMoji</div>
          <p style={{ margin: 0, color: '#3D3A36' }}>
            {hasBrand
              ? 'Sixty seconds and you’ll know where everything lives. Then make your first ad.'
              : 'One thing before anything else works: your brand. Thirty seconds.'}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button data-testid="tour-start" style={btn} onClick={next} autoFocus>
              {hasBrand ? 'Take the tour' : 'Show me'} <ArrowRight style={{ width: 14, height: 14 }} />
            </button>
            <button data-testid="tour-skip" style={ghost} onClick={skip}>Skip — I’ll find my way</button>
          </div>
        </div>
      </>
    );
  }

  // ── Finish ──
  if (isFinish) {
    const prompt = 'Make me 5 ads for Black Friday, include 40% off';
    return (
      <>
        {mask}
        <div data-testid="tour-finish" style={{ ...cardBase, width: 440, left: '50%', top: '50%', transform: 'translate(-50%, -50%)', padding: '22px 24px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>{pin('✓')}<span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>You’re all set</span></div>
          {/* v7.45 — no credit figures here: trial grants change and a paid
              account never had a trial, so any number is wrong for someone. */}
          <p style={{ margin: 0, color: '#3D3A36' }}>Try making some ads:</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button data-testid="tour-prefill" style={{ ...btn, background: PINK, textAlign: 'left', whiteSpace: 'normal', lineHeight: 1.35 }} onClick={() => { onPrefill(prompt); finish(); }}>{prompt}</button>
            <button data-testid="tour-done" style={ghost} onClick={finish}>Done</button>
          </div>
        </div>
      </>
    );
  }

  // ── Anchored pin ──
  const amber = step!.key === 'add-brand';
  const isLast = idx === total - 1;
  const cardStyle: React.CSSProperties = card
    ? { ...cardBase, top: card.top, left: card.left }
    : { ...cardBase, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <>
      {mask}
      <div ref={cardRef} data-testid={`tour-step-${step!.key}`} style={cardStyle} role="dialog" aria-label={step!.title}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {pin(idx + 1, amber)}
            <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>{step!.title}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span data-testid="tour-counter" style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: '#8C8578' }}>{counter}</span>
            <button data-testid="tour-close" onClick={skip} title="Skip tour" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#8C8578', padding: 2, display: 'flex' }}>
              <X style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>
        <VideoSlot file={step!.video} />
        <p style={{ margin: 0, color: '#3D3A36' }}>{step!.body}</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
          {amber ? (
            <>
              <span />
              <button data-testid="tour-add-brand" style={{ ...btn, background: '#9A6414' }} onClick={() => { try { localStorage.removeItem(STEP_KEY); localStorage.removeItem(LEGACY_STEP_KEY); } catch { /* ignore */ } onAddBrand(); onClose(); }}>Add brand</button>
            </>
          ) : (
            <>
              <button data-testid="tour-back" style={{ ...ghost, visibility: idx === 0 ? 'hidden' : 'visible' }} onClick={back}>
                <ArrowLeft style={{ width: 14, height: 14 }} /> Back
              </button>
              <button data-testid="tour-next" style={btn} onClick={next}>
                {isLast ? 'Finish' : 'Next'} <ArrowRight style={{ width: 14, height: 14 }} />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
